import { Property, SearchFilters } from "../types/search";

/**
 * Utility functions for transitioning from server-fetched properties to client-side search
 * and building search filters
 */

/**
 * Resolve the effective `location.city` for a search coming from the filters UI.
 *
 * The filters modal's City input drives the search location: a typed city is
 * used as-is; clearing it while a county filter is set searches the whole county
 * (empty city → backend skips the city clause); clearing it with no county falls
 * back to the page's default city so a search never goes MLS-wide by accident.
 */
export const resolveSearchCity = (
  filters: SearchFilters,
  fallbackCity: string
): string => {
  const modalCity = (filters.filters?.city ?? "").trim();
  if (modalCity) return modalCity;
  if (filters.filters?.county) return ""; // county-wide
  return fallbackCity;
};

/**
 * Build API filters from search parameters
 * This function converts URL search parameters to API filter format
 *
 * @param searchParams - The search parameters from URL
 * @returns API filters object
 */
export const buildApiFilters = (searchParams: any): Record<string, any> => {
  const apiFilters: Record<string, any> = {};

  // Add filters from URL parameters
  if (searchParams.bedrooms)
    apiFilters.bedrooms = parseInt(searchParams.bedrooms);
  if (searchParams.bathrooms)
    apiFilters.bathrooms = parseInt(searchParams.bathrooms);
  if (searchParams.min_price) apiFilters.min_price = searchParams.min_price;
  if (searchParams.max_price) apiFilters.max_price = searchParams.max_price;
  if (searchParams.waterfront) apiFilters.waterfront = true;
  if (searchParams.pool) apiFilters.pool = true;
  if (searchParams.basement) apiFilters.basement = true;
  if (searchParams.garage) apiFilters.garage = true;
  if (searchParams.newconstruction) apiFilters.newconstructionyn = true;
  if (searchParams.rental) apiFilters.rentalyn = true;
  if (searchParams.city_view) apiFilters.city_view = true;
  if (searchParams.mountain_view) apiFilters.mountain_view = true;
  if (searchParams.park_view) apiFilters.park_view = true;
  if (searchParams.water_view) apiFilters.water_view = true;
  if (searchParams.sqft_min) apiFilters.sqft_min = searchParams.sqft_min;
  if (searchParams.sqft_max) apiFilters.sqft_max = searchParams.sqft_max;

  // Add keywords to publicremarks if available
  if (searchParams.keywords && searchParams.keywords.length > 0) {
    apiFilters.publicremarks = searchParams.keywords;
  }

  // Add listing IDs if available (for MLS number search)
  if (searchParams.listing_ids && searchParams.listing_ids.length > 0) {
    apiFilters.listingid = searchParams.listing_ids;
  }

  // Set standard_status from URL parameter or default to Active
  if (searchParams.statuses && searchParams.statuses.length > 0) {
    // If "Active" is selected, automatically include "Active Under Contract"
    if (searchParams.statuses.includes("Active")) {
      const statusSet = new Set(searchParams.statuses);
      statusSet.add("Active Under Contract");
      apiFilters.standard_status = Array.from(statusSet);
    } else {
      apiFilters.standard_status = searchParams.statuses;
    }
  } else {
    // Default to both "Active" and "Active Under Contract"
    apiFilters.standard_status = ["Active", "Active Under Contract"];
  }

  // Set property_sub_type based on URL parameter or default
  apiFilters.property_sub_type = searchParams.property_type;

  // Add subdivision filter if available
  if (searchParams.subdivisions && searchParams.subdivisions.length > 0) {
    apiFilters.subdivision = searchParams.subdivisions;
  }

  // Add ZIP code filter if available
  if (searchParams.zip_code) {
    apiFilters.zip_code = searchParams.zip_code;
  }

  // Add county filter if available
  if (searchParams.county) {
    apiFilters.county = searchParams.county;
  }

  // Check if query contains "condo" to set appropriate property_sub_type
  if (searchParams.query) {
    const queryLower = searchParams.query.toLowerCase();
    if (queryLower.includes("condo")) {
      apiFilters.property_sub_type = "Condominium";
    }
  }

  return apiFilters;
};

/**
 * Load more properties after using server-fetched properties
 * This function handles the transition from server-fetched properties to client-side search
 *
 * @param directSearch - The direct search hook instance
 * @param searchParams - The search parameters from URL
 * @param serverProperties - The server-fetched properties
 * @param configId - The tenant configuration ID
 * @returns A function that loads more properties
 */
export const loadMoreAfterServerProperties = (
  directSearch: any,
  searchParams: any,
  serverProperties: Property[],
  configId: string
) => {
  // Use the utility function to build API filters
  const apiFilters = buildApiFilters(searchParams);

  // Create search parameters with offset
  const searchRequest: SearchFilters & {
    offset?: number;
    number?: number;
    initialServerProperties?: Property[];
  } = {
    query: searchParams.query,
    tenant_id: configId,
    location: {
      city: searchParams.city,
      state: searchParams.state.toUpperCase(),
    },
    filters: apiFilters,
    keywords: searchParams.keywords,
    offset: 20, // Start from page 2 (after server properties)
    number: 20, // Fetch 20 properties per page
    initialServerProperties: serverProperties, // Pass the initial server properties
  };

  console.log("Loading more with offset 20 after server properties");

  // Execute search with direct search hook
  return directSearch.search(searchRequest);
};
